import React, { FC } from 'react';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

interface AvailableLanguage {
  href: string;
  label: string;
}

interface Props {
  availableLanguages: AvailableLanguage[];
}

const sections = [
  { title: 'Visão geral' },
  { title: 'Informações que podemos recolher' },
  { title: 'Informações Biométricas e Exoneração de Responsabilidade' },
  {
    title:
      'De que modo podemos usar e divulgar as Suas Informações Pessoais para prestar Serviços IDV aos clientes',
  },
  {
    title:
      'De que modo podemos utilizar informações anonimizadas ou agregadas para manter, melhorar e desenvolver os Serviços IDV',
  },
  { title: 'Os seus direitos e escolhas' },
  { title: 'Segurança e conservação' },
  { title: 'Atualizações a esta Declaração de Privacidade IDV' },
  { title: 'Contactar a Plaid' },
  { title: 'Para Residentes da União Europeia e do Reino Unido' },
];

const PtVersion: FC<Props> = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='declaracao-de-privacidade-idv'>Declaração de Privacidade IDV</h4>

      <p>Idiomas disponíveis:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Última atualização a 20 de fevereiro de 2026</p>

      <p>
        A Plaid Inc. e as suas subsidiárias, a Plaid Financial Ltd. e a Plaid,
        B.V. (coletivamente, &quot;Plaid&quot;, &quot;nós&quot;,
        &quot;nosso&quot; ou &quot;nos&quot;) oferecem serviços de verificação
        de identidade, deteção de fraude e seguimento de listas de observação
        (os &quot;Serviços IDV&quot; ou &quot;Serviços&quot;) a nível global a
        empresas, sociedades e instituições financeiras (&quot;Clientes&quot; ou
        &quot;Cliente&quot;).
      </p>

      <p>
        Um dos nossos Clientes poderá pedir-lhe que realize várias verificações
        que utilizam os Serviços IDV. A presente declaração de privacidade IDV
        (&quot;Declaração de Privacidade IDV&quot;) explica de que forma os seus
        dados pessoais (&quot;Informações Pessoais&quot;) podem ser utilizados
        quando prestamos os Serviços IDV em nome dos nossos Clientes.
      </p>

      <p>
        A Plaid não recolhe, trata, utiliza ou partilha deliberadamente
        Informações Pessoais de consumidores com menos de 18 anos. Os Serviços
        IDV destinam-se apenas a indivíduos com idade igual ou superior a 18
        anos. Se tiver menos de 18 anos, não utilize os Serviços IDV.
      </p>

      <p>
        Quando prestamos Serviços de IDV aos nossos Clientes, os nossos Clientes
        determinam a finalidade e os meios pelos quais as suas Informações
        Pessoais são tratadas (e os nossos Clientes são considerados um
        responsável pelo tratamento). Agimos de acordo com as instruções dos
        nossos Clientes relativamente ao modo como as suas Informações Pessoais
        são tratadas e às razões subjacentes a tal tratamento. A Plaid é um
        subcontratante ou subcontratante ulterior quando facilita a prestação de
        serviços de verificação da identidade, deteção de fraude e seguimento de
        listas de observação sob as instruções dos nossos Clientes. Os nossos
        Clientes solicitam-nos que recolhamos as suas Informações Pessoais. A
        Plaid atua como subcontratante ou subcontratante ulterior quando lhe é
        solicitado que recolha informações a seu respeito, incluindo as suas
        Informações Pessoais, e a Plaid recebe instruções dos nossos Clientes
        sobre as pessoas a respeito das quais deve recolher dados pessoais e
        sobre quando recolher e tratar as suas Informações Pessoais. Utilizamos
        as suas Informações Pessoais para prestar Serviços IDV aos nossos
        Clientes. Atuando sob as instruções dos Clientes e a pedido destes,
        também lhes transferimos e/ou fornecemos acesso ao descarregamento de
        informações que recolhemos sobre si, incluindo as suas Informações
        Pessoais.
      </p>

      <p>
        Deve sempre consultar as políticas de privacidade dos nossos Clientes.
        Deve ainda endereçar-lhes quaisquer dúvidas e pedidos de exercício dos
        seus direitos em relação às suas Informações Pessoais.
      </p>

      <p>
        Pode aceder diretamente a alguns dos Serviços IDV, incluindo através dos
        nossos sites que fazem referência à presente política (coletivamente
        &quot;Sites&quot;). Podemos ser o controlador de dados em certos casos,
        nomeadamente caso atue enquanto Cliente, caso pretenda colocar uma
        questão sobre os Serviços IDV ou caso visite o nosso site. Descrevemos a
        forma e os motivos pelos quais utilizamos dados pessoais nestas
        circunstâncias nas políticas de privacidade aplicáveis da Plaid,
        disponíveis em{' '}
        <Link href='https://plaid.com/legal/'>
          <a>www.plaid.com/legal/</a>
        </Link>
        .
      </p>

      <p>
        A Plaid nunca venderá a terceiros as suas Informações Pessoais dos
        Serviços IDV. Os nossos Clientes decidem como usamos as suas Informações
        Pessoais, controlam as suas políticas de conservação de dados e podem
        especificar durante quanto tempo conservam as suas Informações Pessoais
        e se e quando podemos partilhar as informações que recolhemos, incluindo
        as suas Informações Pessoais, com o nosso Cliente.
      </p>

      <p>
        Dependendo do serviço solicitado pelos nossos Clientes, poderemos tratar
        as suas Informações Pessoais em nome dos nossos Clientes, conforme
        descrito na presente Declaração de Privacidade IDV, pelo que
        recomendamos que a leia atentamente.
      </p>

      <nav>
        <h5>Ir para a secção:</h5>
        <ol>
          {sections.map((section, index) => {
            return (
              <li key={index}>
                <a href={`#section-pt-${index + 1}`}>{`${section.title}`}</a>
              </li>
            );
          })}
        </ol>
      </nav>

      <div id='section-pt-1'>
        <h5>1. Visão geral</h5>
        <p>
          Quando a Plaid presta os Serviços IDV aos Clientes, poderá ser-lhe
          pedido que nos forneça informações a seu respeito. Em nome dos nossos
          Clientes, prestamos serviços de verificação da identidade baseada na
          nuvem, deteção de fraude e seguimento de listas de observação. Os
          Serviços IDV recolhem informações junto de si e a seu respeito para
          prever e ajudar a prevenir atividades fraudulentas em tempo real. Em
          seguida, a Plaid conclui os seus Serviços IDV e fornece os resultados
          diretamente aos seus Clientes. A pedido do nosso Cliente, fornecemos
          uma cópia ou transferimos as informações que partilha, incluindo as
          suas Informações Pessoais, para utilização do Cliente e no âmbito dos
          Serviços IDV. Atuando sob as instruções dos Clientes, podemos
          transferir e/ou fornecer acesso ao descarregamento de informações que
          recolhemos sobre si, incluindo as suas Informações Pessoais.
        </p>
        <p>
          Os nossos Clientes determinam o âmbito do pedido de informações e que
          informações pessoais a seu respeito podem ser utilizadas para análise
          com recurso aos Serviços IDV. Os clientes configuram os Serviços IDV
          para exigir e utilizar determinadas informações para verificar a sua
          identidade. Um Cliente pode configurar os Serviços IDV de modo a que
          sejam solicitadas informações limitadas. Se as informações solicitadas
          forem insuficientes para executar a verificação, mediante instruções
          do nosso Cliente, os Serviços IDV podem solicitar informações
          adicionais para concluir o processo de verificação.
        </p>
        <p>
          Se não estiver satisfeito com o resultado de qualquer operação de
          tratamento, deve encaminhar a sua questão para o Cliente.
        </p>
      </div>

      <div id='section-pt-2'>
        <h5>2. Informações que podemos recolher</h5>
        <p>
          Dependendo dos Serviços IDV solicitados pelos nossos Clientes, podemos
          recolher as suas Informações Pessoais das seguintes formas:
        </p>
        <p>
          <strong>Informações que fornece:</strong> Recolhemos as suas
          Informações Pessoais quando nos fornece informações solicitadas pelo
          Cliente. Se optar por não fornecer as informações solicitadas, não
          seremos capazes de prestar os Serviços IDV ao nosso Cliente. Contacte
          o Cliente para discutir opções alternativas de verificação e triagem
          da identidade, caso estejam disponíveis para a sua região ou
          nacionalidade.
        </p>
        <p>
          Algumas das potenciais informações sobre si que podem ser solicitadas
          em nome dos nossos Clientes incluem dados de contacto, incluindo o seu
          nome, endereço de e-mail, endereço postal, número de telefone, data de
          nascimento; documentos de identidade com a sua imagem; o seu número de
          identidade atribuído pelo governo, como um número de segurança social;
          e/ou uma fotografia ou gravação de vídeo sua, para avaliar se uma
          pessoa corresponde ao seu documento de identidade e se aparenta ser
          uma pessoa viva.
        </p>
        <p>
          Recolhemos dados diretamente junto de si através de técnicas padrão do
          setor, como o código JavaScript, SDK (conjuntos de desenvolvimento de
          software, do inglês &quot;software development kit&quot;), envios de
          formulários, recolha de imagens, recolha de vídeos e comportamentos,
          utilizando ferramentas que os nossos Clientes incorporam nos seus
          sites ou aplicações móveis (
          <strong>&quot;Sites do Cliente&quot;</strong>
          ).
        </p>
        <p>
          <strong>
            Informações utilizadas para verificar um número de telefone:
          </strong>{' '}
          Também podemos disponibilizar aos nossos Clientes uma funcionalidade
          de autenticação de dois fatores, para assegurar medidas que garantam
          que a pessoa que fornece as informações está na posse do número de
          telefone associado a essa identidade. Para disponibilizar esta
          funcionalidade aos nossos Clientes, poderemos utilizar as suas
          Informações Pessoais, conforme fornecidas pelo nosso Cliente ou por
          si, para lhe enviar códigos de verificação, tais como mensagens de
          texto, que pode depois introduzir para confirmar a sua identidade
          quando utiliza um Site do Cliente ou para criar uma nova conta junto
          de um Cliente.
        </p>
        <p>
          <strong>
            Informações que recolhemos automaticamente quando visita os Sites do
            Cliente:
          </strong>{' '}
          Os Serviços IDV utilizam determinadas tecnologias de rastreio padrão
          para recolher automaticamente determinadas informações sobre o seu
          dispositivo quando interage com e utiliza os Sites do Cliente,
          incluindo análises comportamentais no que toca à sua interação com os
          Serviços IDV, repetições de sessões e a frequência com que um
          dispositivo é utilizado para interagir com Sites do Cliente e com os
          Serviços IDV. Algumas destas informações incluem, por exemplo, o seu
          endereço IP, e determinados identificadores únicos podem identificar
          um computador ou dispositivo específicos e podem ser considerados
          Informações Pessoais em algumas jurisdições, incluindo na UE.
        </p>
        <p>
          Os tipos de informações que podemos recolher em nome dos nossos
          Clientes dependerão de se está a visitar um Site do Cliente através de
          uma interface API, de uma aplicação que utiliza o nosso SDK ou de uma
          página Web, incluindo:
        </p>
        <ul>
          <li>
            <strong>Informações do navegador e do dispositivo</strong>,
            incluindo tipo e modelo do dispositivo, fabricante, tipo e versão do
            sistema operativo, tipo e versão do navegador web, agente do
            utilizador, nome e informações da operadora telefónica, fuso
            horário, tipo de ligação de rede, endereço IP, identificadores
            baseados em hardware, nome do anfitrião, identificadores do
            dispositivo, ID de Publicidade Android/Google, impressão digital da
            tela, características relacionadas com emulação ou
            &quot;rooting&quot; (desbloqueamento), como o facto de o seu
            dispositivo ter sido sujeito a um &quot;jailbreak&quot; e o nome e
            versão da aplicação. Também recolhemos informações sobre conjuntos
            de caracteres, o nome do anfitrião, a língua, o título da página e o
            URL, o URL do referenciador, o número de tipos de letra, o valor de
            dispersão dos tipos de letra, o número de plugins, o valor de
            dispersão dos plugins, a altura e largura do ecrã, a profundidade da
            cor, a plataforma, a pegada de cookies, os pontos de toque máximos,
            a compatibilidade com o Java, o armazenamento de sessão, o
            armazenamento local, se a resolução foi adulterada, a língua ou SO,
            se o bloqueio de anúncios está ativado, se a definição «não
            rastrear» está ativada, a IP da ligação flash e o identificador do
            flash. O SDK também recolherá metadados relacionados com o
            telemóvel, tais como o nível de bateria, as propriedades do
            dispositivo e o nome da operadora.
          </li>
          <li>
            <strong>
              Informações sobre o seu comportamento nos Sites do Cliente
            </strong>
            , ou seja, informações sobre as suas atividades nos Sites do
            Cliente, ID da sessão, hora de início e fim da sessão, compensação
            do fuso horário e informações de localização que podem ser
            informações gerais de localização inferidas a partir do seu endereço
            IP ou, em algumas circunstâncias, informações de geolocalização mais
            precisas com base nas coordenadas de latitude e longitude. Poderá
            ser capaz de controlar a recolha de informações de localização
            através de Sites de Clientes específicos alterando as preferências
            no seu dispositivo móvel.
          </li>
        </ul>
        <p>
          <strong>
            De que modo utilizamos tecnologias de rastreio para recolher
            informações sobre si.
          </strong>{' '}
          Utilizamos tecnologias de rastreio padrão para recolher
          automaticamente determinadas informações do seu dispositivo e
          navegador quando visita ou interage com Sites do Cliente. Quando
          visualiza ou utiliza os Serviços IDV através de um Site do Cliente, os
          servidores da Plaid são notificados e podemos recolher informações do
          navegador ou aplicação. Além disso, recolhemos dados diretamente junto
          de si através de tecnologias de rastreio padrão, como o JavaScript ou
          o SDK, que os nossos Clientes podem incorporar nos seus sites.
        </p>
        <p>Os tipos de tecnologias de rastreio que podemos utilizar incluem:</p>
        <ul>
          <li>
            <strong>O JavaScript Code (JS Snippet)</strong>, também designado
            tag ou pixel, é um pequeno fragmento de código inserido no conteúdo
            do Site do Cliente. Pode desativar o JavaScript alterando as
            definições no seu navegador. O site do seu fornecedor de navegadores
            de Internet contém normalmente informações sobre como alterar essas
            definições. Uma vez que a Plaid não controla estas definições,
            encorajamo-lo a verificar as informações fornecidas regularmente
            para garantir que está ciente de quaisquer alterações relevantes.
          </li>
          <li>
            <strong>
              SDK (Conjuntos de Desenvolvimento de Software) Móveis
            </strong>{' '}
            ou blocos de código incorporados num Site do Cliente que permitem à
            Plaid recolher determinadas informações. Pode controlar a utilização
            de determinadas informações que a Plaid recolhe através do SDK
            seguindo as instruções aplicáveis ao sistema operativo do seu
            dispositivo móvel. Também poderá ser capaz de controlar se e de que
            modo o seu dispositivo móvel fornece informações de localização em
            Sites de Clientes específicos, alterando as preferências no seu
            dispositivo móvel. Uma vez que a Plaid não controla estas
            definições, encorajamo-lo a verificar as informações fornecidas
            regularmente para garantir que está ciente de quaisquer alterações
            relevantes.
          </li>
          <li>
            <strong>Impressão digital de tela</strong>, uma técnica de rastreio
            que nos permite reproduzir imagens gráficas a partir das
            funcionalidades incorporadas do Canvas da HTML5 no seu navegador. A
            imagem da tela é frequentemente reproduzida de forma diferente em
            diferentes dispositivos devido a vários fatores, tais como a versão
            do seu navegador web, o sistema operativo e as respetivas
            definições, e o hardware gráfico instalado, e permite-nos
            distingui-lo de outros utilizadores. Se pretender desativar a
            impressão digital de tela, pode optar por desativar o JavaScript ou
            transferir uma extensão do navegador que bloqueie as técnicas de
            impressão digital de tela, mas fazê-lo pode prejudicar a
            funcionalidade dos Serviços IDV. Uma vez que a Plaid não controla
            estas definições ou extensões, encorajamo-lo a verificar
            regularmente as informações fornecidas sobre as mesmas, para
            garantir que está ciente de quaisquer alterações relevantes.
          </li>
          <li>
            <strong>Impressão digital do dispositivo</strong>, uma técnica de
            rastreio que nos permite identificá-lo utilizando informações
            relacionadas com o software e hardware do seu dispositivo, incluindo
            componentes da impressão digital da sua tela, combinada com as
            informações do seu dispositivo. A Plaid utiliza a impressão digital
            do dispositivo para combater fraudes e outros agentes nocivos, bem
            como para fins de cibersegurança.
          </li>
        </ul>
        <p>
          Algumas destas informações como, por exemplo, o seu endereço IP, e
          determinados identificadores únicos, podem identificar um computador
          ou dispositivo específicos e, por conseguinte, podem ser consideradas
          &quot;Informações Pessoais&quot; em algumas jurisdições, incluindo na
          UE e na Califórnia.
        </p>
        <p>
          <strong>
            Informações que recolhemos prestadores de serviços e de fontes
            terceiras:
          </strong>{' '}
          A pedido do nosso Cliente e no âmbito dos Serviços IDV que lhe
          prestamos, podemos combinar as informações que recolhemos sobre si com
          informações que recebemos de prestadores de serviços. Por exemplo,
          podemos receber informações como, por exemplo, se um endereço IP é
          comercial ou privado, se um número de telefone corresponde a um
          telefone fixo ou se um domínio de e-mail é gratuito. Os clientes
          também podem pedir-nos para trabalhar com terceiros que fazem a
          correspondência entre as informações fornecidas e fontes de terceiros,
          tais como ficheiros de cabeçalhos de relatórios de crédito, registos
          de contas móveis, registos de serviços públicos, registos de veículos
          motorizados e outras fontes respeitáveis. Em determinados países,
          poderá ser pedido à sua operadora móvel que divulgue os dados da sua
          conta móvel com o objetivo de verificar a sua identidade, incluindo o
          seu nome, morada e dados do dispositivo. Também podemos receber
          informações sobre si de terceiros e/ou recolher informações sobre si
          que estejam publicamente disponíveis, em nome do nosso Cliente.
        </p>
        <p>
          <strong>Categorias especiais de dados pessoais.</strong> Se os nossos
          Clientes exigirem que nos forneça um documento que contenha a sua
          fotografia ou se precisar de verificar a sua identidade fornecendo uma
          fotografia ou uma gravação de vídeo seus, estas imagens poderão
          revelar categorias especiais de dados sobre si, nomeadamente
          informações relacionadas com a sua saúde (por exemplo, se usar
          óculos), a sua raça ou etnia ou relacionadas com as suas crenças
          religiosas ou políticas. A utilização destas categorias especiais de
          dados pessoais é única e exclusivamente efetuada de acordo com as
          instruções do nosso Cliente e em conformidade com qualquer uma das
          políticas de privacidade dos nossos Clientes e com a presente
          Declaração de Privacidade IDV. A Plaid IDV não vende Informações
          Pessoais.
        </p>
      </div>

      <div id='section-pt-3'>
        <h5>3. Informações Biométricas e Exoneração de Responsabilidade</h5>
        <p>
          Se o nosso Cliente solicitar que nos forneça um documento de
          identidade que contenha uma imagem do seu rosto e uma fotografia ou
          gravação de vídeo (por vezes chamada selfie), poderemos comparar a sua
          geometria facial derivada da imagem constante do seu documento de
          identidade com a sua geometria facial derivada da fotografia ou
          gravação de vídeo que fornece para determinar se uma pessoa
          corresponde ao seu documento de identidade. Também podemos usar a
          digitalização da sua geometria facial para detetar e prevenir fraudes,
          incluindo para determinar se a gravação de vídeo é de uma pessoa viva
          e/ou para determinar a sua idade estimada, se for caso disso. A imagem
          do seu documento de identidade, fotografia ou gravação de vídeo, e
          quaisquer digitalizações da geometria facial derivadas dessas imagens,
          podem ser consideradas informações biométricas em determinadas
          jurisdições.
        </p>
        <p>
          Ao utilizar os Serviços IDV para se ligar a uma aplicação ou a um
          serviço fornecido pelo nosso Cliente, concorda que leu, compreendeu e
          consentiu voluntariamente com{' '}
          <Link href='#biometric-policy-and-release'>
            <a>
              a Política Biométrica e a Exoneração de Responsabilidade da Plaid
            </a>
          </Link>
          , e mantém a Plaid e os respetivos prestadores de serviços indemnes
          face a quaisquer reclamações relacionadas com a recolha, o tratamento,
          a utilização, a divulgação ou a partilha das suas imagens, fotografias
          ou gravações de vídeo, incluindo quaisquer dados derivados dessas
          imagens para criar digitalizações de geometria facial.
        </p>
        <p>
          Para obter mais informações sobre a nossa recolha, utilização,
          tratamento, divulgação e conservação da sua imagem do seu documento de
          identidade, fotografia ou gravação de vídeo, incluindo eventuais
          digitalizações de geometria facial derivadas dessas imagens que possam
          ser consideradas dados biométricos na sua região, consulte{' '}
          <Link href='#biometric-policy-and-release'>
            <a>
              a Política Biométrica e Exoneração de Responsabilidade da Plaid
            </a>
          </Link>
          .
        </p>
      </div>

      <div id='section-pt-4'>
        <h5>
          4. De que modo podemos usar e divulgar as Suas Informações Pessoais
          para prestar Serviços IDV aos clientes
        </h5>
        <p>
          Os nossos Clientes podem recorrer aos nossos Serviços IDV, após o que
          lhes devolveremos informações sobre a probabilidade de a pessoa que
          fornece as informações ser quem afirma ser. Atuando sob as instruções
          dos Clientes e a pedido destes, também lhes transferimos e/ou
          fornecemos acesso ao descarregamento de informações que recolhemos
          sobre si, incluindo as suas Informações Pessoais.
        </p>
        <p>
          A Plaid utiliza as suas Informações Pessoais para prestar os Serviços
          IDV aos seus Clientes e para ajudá-los a cumprir as suas obrigações
          legais. Também podemos utilizar informações do dispositivo ou
          tecnologias de rastreio para identificar e prevenir fraudes nos
          Serviços IDV. A título de exemplo, recorrendo ao nosso serviço de
          verificação de identidade baseado na nuvem, tratamos as suas
          Informações Pessoais para devolver aos nossos Clientes informações
          sobre correspondências relativas a eventos ou atividades específicos
          no Site do Cliente. Além disso, quando os nossos Clientes utilizam
          autenticação de dois fatores, tratamos as suas Informações Pessoais,
          como o seu número de telefone, para lhe enviar um código de
          verificação por mensagem de texto. Isto ajuda os nossos Clientes que
          utilizam esta funcionalidade a verificar as identidades, garantindo a
          titularidade do número de telefone associado a identidades.
        </p>
      </div>

      <div id='section-pt-5'>
        <h5>
          5. De que modo podemos utilizar informações anonimizadas ou agregadas
          para manter, melhorar e desenvolver os Serviços IDV
        </h5>
        <p>
          Podemos anonimizar, desidentificar ou agregar informações para manter,
          melhorar, otimizar ou desenvolver os Serviços IDV. Também podemos
          utilizar informações anonimizadas, desidentificadas ou agregadas para
          prevenir e detetar fraudes nos Serviços IDV. A Plaid IDV nunca utiliza
          as suas Informações Pessoais para manter, melhorar, otimizar ou
          desenvolver os Serviços IDV.
        </p>
      </div>

      <div id='section-pt-6'>
        <h5>6. Os seus direitos e escolhas</h5>
        <p>
          Dependendo da sua localização e sob reserva da lei aplicável, poderá
          beneficiar de direitos no que se refere às suas Informações Pessoais
          que tratamos em nome do Cliente.
        </p>
        <p>
          <strong>Direitos de Proteção de Dados.</strong> Quando atuamos como
          subcontratante, subcontratante ulterior ou prestador de serviços em
          nome dos nossos Clientes, têm de enviar ao Cliente qualquer pedido
          para aceder às suas Informações Pessoais ou para exercer qualquer um
          dos seus direitos de proteção de dados. Iremos ajudar os nossos
          Clientes a responder ao seu pedido e agiremos de acordo com as suas
          instruções.
        </p>
        <p>
          Consulte a Política de Privacidade do Utilizador Final da Plaid em{' '}
          <Link href='https://plaid.com/legal/#consumers'>
            <a>https://plaid.com/legal/#consumers</a>
          </Link>{' '}
          para mais pormenores sobre a forma como lidamos com pedidos no âmbito
          dos quais sejamos responsáveis pelo tratamento de quaisquer dados
          pessoais, na qualidade de responsável pelo tratamento de dados.
        </p>
      </div>

      <div id='section-pt-7'>
        <h5>7. Segurança e conservação</h5>
        <p>
          <strong>Garantias de segurança.</strong> Utilizamos medidas de
          segurança técnicas e organizativas concebidas para proteger as
          Informações Pessoais tratadas no âmbito dos Serviços IDV contra o
          acesso, a divulgação, a alteração e a destruição não autorizados.
        </p>
        <p>
          <strong>Conservação de dados.</strong> Os clientes controlam as suas
          políticas de conservação de dados e podem especificar durante quanto
          tempo conservam as suas Informações Pessoais. Quando um Cliente no-lo
          solicitar, as suas Informações Pessoais serão eliminadas ou
          anonimizadas.
        </p>
      </div>

      <div id='section-pt-8'>
        <h5>8. Atualizações a esta Declaração de Privacidade IDV</h5>
        <p>
          Podemos rever a Declaração de Privacidade IDV ocasionalmente em
          resposta aos requisitos dos nossos Clientes no que se refere aos
          Serviços IDV, bem como a desenvolvimentos jurídicos, técnicos ou
          comerciais. As atualizações serão fornecidas no nosso Site e a versão
          revista entrará em vigor quando for publicada. Se fizermos alterações
          substanciais ao modo como utilizamos ou partilhamos Informações
          Pessoais recolhidas anteriormente junto de si, publicaremos a versão
          atualizada aqui. Para ver quando é que a presente Declaração de
          Privacidade IDV foi atualizada pela última vez, verifique a data da
          &quot;última atualização&quot; ou &quot;entrada em vigor&quot;
          apresentada no topo desta página.
        </p>
      </div>

      <div id='section-pt-9'>
        <h5>9. Contactar a Plaid</h5>
        <p>
          Para quaisquer questões ou comentários sobre a presente Declaração de
          Privacidade IDV ou sobre as nossas práticas de privacidade, pode
          contactar a Plaid através do endereço{' '}
          <Link href='mailto:privacy@plaid.com'>
            <a>privacy@plaid.com</a>
          </Link>{' '}
          ou enviando uma carta para:
        </p>
        <p>
          Plaid Inc.
          <br />
          Ao cuidado de: Legal
          <br />
          PO Box 7775 #35278
          <br />
          San Francisco, California 94120-7775
          <br />
          EUA
        </p>
      </div>

      <div id='section-pt-10'>
        <h5>10. Para Residentes da União Europeia e do Reino Unido</h5>
        <p>
          A Plaid nomeou a Plaid B.V. como seu Representante para a Proteção de
          Dados para efeitos do RGPD na UE/EEE e da Lei de Proteção de Dados de
          2018 (conforme alterada) no Reino Unido. Se for residente na UE/EEE ou
          no Reino Unido e tivermos tratado ou estivermos a tratar as Suas
          Informações Pessoais, poderá ter o direito de exercer os seus direitos
          ao abrigo do RGPD.
        </p>
        <p>
          Se o nosso Cliente em cujo nome tratámos as suas Informações Pessoais
          também estiver localizado no EEE ou no Reino Unido, deve contactá-lo a
          ele, e não ao Representante para a Proteção de Dados da Plaid, para
          quaisquer questões relacionadas com as suas Informações Pessoais. Se
          ainda assim quiser colocar uma questão à Plaid, ou de outra forma
          exercer os seus direitos no que se refere às suas Informações
          Pessoais, pode fazê-lo:
        </p>
        <ul>
          <li>enviando um e-mail para privacy@plaid.com; ou</li>
          <li>
            contactando o nosso Representante para a Proteção de Dados através
            do formulário web disponível em{' '}
            <Link href='https://plaid.com/legal/data-protection-request-form/'>
              <a>https://plaid.com/legal/data-protection-request-form/</a>
            </Link>
            .
          </li>
        </ul>
        <p>
          Também pode enviar a sua questão por correio para a Plaid, para os
          seguintes endereços:
        </p>
        <p>
          <em>Se residir no Reino Unido:</em>
        </p>
        <p>
          Plaid Financial Ltd.
          <br />
          Ao cuidado de: Legal
          <br />
          Techspace Level 4, 25 Worship Street
          <br />
          London, Reino Unido, EC2A 2DX
        </p>
        <p>
          <em>Se residir no EEE:</em>
        </p>
        <p>
          Plaid B.V.
          <br />
          Ao cuidado de: Legal
          <br />
          Muiderstraat 1<br />
          1011PZ Amsterdam
          <br />
          Países Baixos
        </p>
        <p>
          Ao receber a sua correspondência, é provável que a Plaid solicite
          provas da sua identidade, para garantir que as suas Informações
          Pessoais não são fornecidas a ninguém além de si.
        </p>
      </div>

      <hr className='section-divider' />
    </>
  );
};

export default PtVersion;
